import { useEffect, useMemo, useState } from 'react';
import { first, noop, isEmpty, sortBy, flatMap, last, map, compact } from 'lodash';
import {
  api,
  getCreateSurveyAnswersUrl,
  getNextSurveyQuestionUrl,
  getPrevSurveyQuestionUrl,
  getSurveysListUrl,
  getCurrentSurveyQuestionUrl,
  useRequest,
  QuestionDto,
  PatientSurveyDto,
  SurveyStatuses,
  SurveyTypesEnum,
} from '@monument/core';

type Questions = {
  questions: QuestionDto[] | null;
};

const getSurveyByType = type =>
  api
    .get(getSurveysListUrl(), { params: { type } })
    .then(({ data }) => {
      if (data) {
        return data;
      }
      throw new Error('No Results');
    })
    .catch(() => {
      throw new Error('Unable to get the survey');
    });

const getQuestionsForRequestBody = questionToAnswersMap => {
  return map(questionToAnswersMap, (answers, questionId) => ({
    questionId: parseInt(questionId),
    answerIds: answers.map(a => a.id),
    answer: compact(flatMap(answers.map(a => a.value))),
  }));
};

/**
 * Sorts questions by order if they are not null or undefiend. Questions can be
 * explicitly null from the backend denoting that there are no more questions for
 * the current survey and a transition needs to be executed.
 * @param questions
 * @returns
 */
const useSortedQuestion = (questions: QuestionDto[] | null | undefined) => {
  const sortedQuestions = useMemo(
    () => (questions ? sortBy(questions, 'order') : undefined),
    [questions],
  );

  return sortedQuestions;
};

const useFetchCurrentQuestion = ({ surveyId }) => {
  const {
    data: currentQuestions,
    loading: currentQuestionsLoading,
    run: fetchCurrentQuestions,
  } = useRequest<Questions>(getCurrentSurveyQuestionUrl(surveyId), { manual: true });

  return {
    currentQuestions: useSortedQuestion(currentQuestions?.questions),
    currentQuestionsLoading,
    fetchCurrentQuestions,
  };
};

const useFetchPrevQuestion = ({ surveyId, onBack, firstQuestionId }) => {
  const {
    data: prevQuestions,
    loading: prevQuestionsLoading,
    run: fetchPrevQuestions,
  } = useRequest<Questions>(getPrevSurveyQuestionUrl(surveyId, firstQuestionId), {
    manual: true,
  });

  useEffect(() => {
    if (prevQuestions && prevQuestions.questions === null) {
      onBack();
    }
  }, [prevQuestions]);

  return {
    prevQuestions: useSortedQuestion(prevQuestions?.questions),
    prevQuestionsLoading,
    fetchPrevQuestions,
  };
};

const useSaveSurveyAnswers = ({ surveyId, lastQuestionId, onFinish }) => {
  const { loading: saveAnswersLoading, run: saveAnswers } = useRequest<PatientSurveyDto>(
    questionToAnswersMap => ({
      url: getCreateSurveyAnswersUrl(surveyId),
      method: 'post',
      data: {
        questions: getQuestionsForRequestBody(questionToAnswersMap),
      },
    }),
    {
      manual: true,
      onSuccess: (survey, params) => {
        const isSurveyFinished = survey.status === SurveyStatuses.Finished;

        if (isSurveyFinished) {
          onFinish();
        } else {
          fetchNextQuestions(params[1]);
        }
      },
    },
  );

  const {
    data: nextQuestions,
    loading: nextQuestionsLoading,
    run: fetchNextQuestions,
  } = useRequest<Questions>(getNextSurveyQuestionUrl(surveyId, lastQuestionId), {
    manual: true,
  });

  useEffect(() => {
    if (!nextQuestions) {
      return;
    }

    if (nextQuestions.questions === null) {
      onFinish();
    }
  }, [nextQuestions]);

  const questions = useSortedQuestion(nextQuestions?.questions);

  return {
    nextQuestions: questions,
    loading: nextQuestionsLoading || saveAnswersLoading,
    saveAnswersAndContinue: saveAnswers,
  };
};

const useHandleSurveyQuestions = ({ onFinish, onBack, surveyId }) => {
  const [questions, setQuestions] = useState<QuestionDto[] | undefined>(undefined);

  const { currentQuestions, currentQuestionsLoading, fetchCurrentQuestions } =
    useFetchCurrentQuestion({
      surveyId,
    });

  const { prevQuestions, prevQuestionsLoading, fetchPrevQuestions } = useFetchPrevQuestion({
    surveyId,
    onBack,
    firstQuestionId: first(questions)?.id,
  });

  const {
    nextQuestions,
    loading: nextQuestionsLoading,
    saveAnswersAndContinue,
  } = useSaveSurveyAnswers({
    surveyId,
    onFinish,
    lastQuestionId: last(questions)?.id,
  });

  useEffect(() => {
    setQuestions(currentQuestions);
  }, [currentQuestions]);

  useEffect(() => {
    setQuestions(prevQuestions);
  }, [prevQuestions]);

  useEffect(() => {
    setQuestions(nextQuestions);
  }, [nextQuestions]);

  return {
    saveAnswersAndContinue,
    fetchCurrentQuestions,
    fetchPrevQuestions,
    processing: Boolean(currentQuestionsLoading || prevQuestionsLoading || nextQuestionsLoading),
    questions,
  };
};

const useContinueSurvey = (enabled, continueSurvey) => {
  const [triggered, setTriggered] = useState(false);

  useEffect(() => {
    if (!enabled || triggered) {
      return;
    }

    // fetching question after reload
    setTriggered(true);
    continueSurvey();
  }, [enabled, triggered, continueSurvey]);
};

export const useSurvey = (
  typeId: SurveyTypesEnum,
  props: { onFinish?: () => void; onBack?: () => void } = {},
) => {
  const { onFinish = noop, onBack = noop } = props;
  const [loading, setLoading] = useState(false);
  const [survey, setSurvey] = useState({});
  const {
    saveAnswersAndContinue,
    fetchCurrentQuestions,
    fetchPrevQuestions,
    processing,
    questions,
  } = useHandleSurveyQuestions({
    onFinish,
    onBack,
    surveyId: survey?.ongoingSurveyId,
  });

  // NOTE: It is possible for multiple questions to be fetched at the same time during
  // the medical intake because some have follow up questions attached, but
  // isLastQuestion happens to work because the last intake question only returns itself
  const isLastQuestion = first(questions)
    ? first(questions).questionTotals - first(questions).questionsAnswered === 1
    : false;
  const progressText = first(questions)
    ? `Question ${first(questions).questionsAnswered + 1} / ${first(questions).questionTotals}`
    : '';

  useEffect(() => {
    setLoading(true);
    getSurveyByType(typeId).then(result => {
      setSurvey(result);
      setLoading(false);
    });
  }, [typeId]);

  const isGetSurveyAfterReload = survey.ongoingSurveyId && isEmpty(questions);
  useContinueSurvey(isGetSurveyAfterReload, fetchCurrentQuestions);

  return {
    id: survey.ongoingSurveyId,
    finished: !!survey.finishedSurveyId,
    loading: loading || processing,
    saveAnswersAndContinue,
    fetchPrevQuestions,
    questions,
    isLastQuestion,
    progressText,
  };
};
